import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../assets/styles/waitingListLead/WaitingListLeadOverview.scss";
import EmailModal from "./EmailModal";
import NoteModal from "./NoteModal";
import WaitingListLeadStateClosed from "./WaitingListLeadStateClosedV1";
import { WaitingListLeadState } from "../types/enums";
import ApiError from "../../../../lib/ApiError";
import BackToTopButton from "../../../../components/ui/buttons/BackToTopButton";
import WaitingListLeadStateAcquiredV1 from "./WaitingListLeadStateAcquiredV1";
import WaitingListLeadStateOpenV1 from "./WaitingListLeadStateOpenV1";
import WaitingListLeadStateRejectedV1 from "./WaitingListLeadStateRejectedV1";
import WaitingListLeadStateAcquiredWithClosedDateV1 from "./WaitingListLeadStateAcquiredWithClosedDateV1";
import { useSupplierInfo } from "layout/context/SupplierInfoContext";
import { isRegistrationForAm } from "constants/index";
import { getCommaSeparatedPhoneNumberFromArray } from "lib/leadNumber/phoneNumber";
import WaitingListLeadBasicInformationTableV1 from "./WaitingListLeadBasicInformationTableV1";
import WaitingListLeadProductCriteriaInformationTableV1 from "./WaitingListLeadProductCriteriaInformationTableV1";
import GroupSection from "./GroupSection";
import ProductDetailCard from "./ProductDetailCard";
import { ILeadPhoneNumber } from "types";
import CollapsibleGroup from "./CollapsibleGroup";
import CallModal from "./CallModal";
import TaskModal from "./TaskModal";

// Definition eines Activity-Interfaces
interface Activity {
  icon: string;
  time: string;
  text: string;
}

const mockData = {
  taskDetailsInfo: {
    title: "Rückruf vereinbaren",
    status: "In Bearbeitung",
    priority: "Hoch",
    dueDate: "15.04.2023",
    assignedTo: "Max Mustermann",
    description: "Kunde wünscht einen Rückruf zur Beratung über Kurzzeitpflege",
    createdAt: "10.04.2023",
    clientName: "Max Mustermann",
    clientAvailability: "Mo-Fr 9-17 Uhr",
    clientLocation: "Mainz",
    phoneNumbers: [
      { type: "mobile", number: "017621231523", internationalNoBlanks: 176826378123 },
    ] as unknown as ILeadPhoneNumber[],
    taskState: WaitingListLeadState.Acquired,
    isClosedByClient: false,
    isPersonalProduct: false,
    relatedProduct: "Kurzzeitpflege",
    requestRegion: "Region XY",
    requestZipCode: "12345",
    taskCriteriaList: [
      { criteriaName: "Kategorie", value: "Beratungsgespräch" },
      { criteriaName: "Follow-Up", value: "Erforderlich" }
    ],
    taskNumber: "T-123456"
  },
  relatedItems: [
    {
      title: "Beratungsgespräch",
      detail: "Status: Ausstehend",
      onOptionsClick: () => alert("Optionen für Beratungsgespräch")
    },
    {
      title: "Angebotserstellung",
      detail: "Status: Nicht gestartet",
      onOptionsClick: () => alert("Optionen für Angebotserstellung")
    }
  ],
  emailItems: [
    {
      title: "Termin-Bestätigung",
      detail: "Gesendet: 12.04.2023",
      onOptionsClick: () => alert("Optionen für Termin-Bestätigung")
    },
    {
      title: "Informationsmaterial",
      detail: "Gesendet: 13.04.2023",
      onOptionsClick: () => alert("Optionen für Informationsmaterial")
    }
  ],
  nextActionItems: [
    {
      title: "Follow-Up Anruf",
      detail: "Geplant: 17.04.2023",
      onOptionsClick: () => alert("Optionen für Follow-Up Anruf")
    },
    {
      title: "Angebot erstellen",
      detail: "Geplant: 18.04.2023",
      onOptionsClick: () => alert("Optionen für Angebot erstellen")
    }
  ],
  // initiale Aktivitäten aus Mock-Daten
  tabContent: {
    activities: [
      { icon: "fa-solid fa-tasks", time: "10:30", text: "Aufgabe erstellt" },
      { icon: "fa-solid fa-user-check", time: "11:15", text: "Max Mustermann zugewiesen" },
    ],
    notes: { title: "Notizen", text: "Hier kannst du alle Notizen zur Aufgabe einsehen." },
    emails: { title: "E‑Mails", text: "E-Mail Verlauf wird hier angezeigt." },
    calls: { title: "Anrufe", text: "Liste der Anrufe findest du hier." },
    tasks: { title: "Unteraufgaben", text: "Unteraufgaben werden hier dargestellt." },
    appointments: { title: "Termine", text: "Hier werden alle geplanten Termine angezeigt." },
    taskDetailsTitle: "Aufgabendetails"
  }
};

interface TaskInfo {
  title: string;
  status: string;
  priority: string;
  dueDate: string;
  assignedTo: string;
  clientName: string;
  clientAvailability: string;
  phoneNumbers: ILeadPhoneNumber[];
  email?: string;
}

// Angepasste MiddleSection für Task-Details
interface MiddleSectionProps {
  taskInfo: TaskInfo | null;
  activities: Activity[];
  getWaitingListLeadStatusView: (
    state: WaitingListLeadState,
    isClosedByClient: boolean,
    isPersonalProduct: boolean
  ) => JSX.Element;
}

const MiddleSection: React.FC<MiddleSectionProps> = ({
  taskInfo,
  activities,
  getWaitingListLeadStatusView
}) => {
  const [activeTab, setActiveTab] = useState("aktivität");

  return (
    <div className="middle-section">
      <div className="search-bar">
        <input type="text" placeholder="Suche..." />
      </div>
      <div className="tabs">
        {["aktivität", "aufgabendetails", "notizen", "e-Mails", "anrufe", "unteraufgaben", "termine"].map(tab => (
          <button
            key={tab}
            className={activeTab === tab ? "active" : ""}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
        <div className="filter-options">
          <label htmlFor="filter-select">Filter:</label>
          <select id="filter-select">
            <option value="alle">Alle</option>
            <option value="heute">Heute</option>
            <option value="dieseWoche">Diese Woche</option>
          </select>
        </div>
      </div>
      <div className="tab-content">
        {activeTab === "aktivität" && (
          <div className="activities">
            <h2>Aktivitäten</h2>
            {activities.map((activity, index) => (
              <div key={index} className="activity">
                <i className={activity.icon} /> {activity.time} - {activity.text}
              </div>
            ))}
          </div>
        )}
        {activeTab === "notizen" && (
          <div className="notes">
            <h2>{mockData.tabContent.notes.title}</h2>
            <p>{mockData.tabContent.notes.text}</p>
          </div>
        )}
        {activeTab === "e-Mails" && (
          <div className="emails">
            <h2>{mockData.tabContent.emails.title}</h2>
            <p>{mockData.tabContent.emails.text}</p>
          </div>
        )}
        {activeTab === "anrufe" && (
          <div className="calls">
            <h2>{mockData.tabContent.calls.title}</h2>
            <p>{mockData.tabContent.calls.text}</p>
          </div>
        )}
        {activeTab === "unteraufgaben" && (
          <div className="tasks">
            <h2>{mockData.tabContent.tasks.title}</h2>
            <p>{mockData.tabContent.tasks.text}</p>
          </div>
        )}
        {activeTab === "termine" && (
          <div className="appointments">
            <h2>{mockData.tabContent.appointments.title}</h2>
            <p>{mockData.tabContent.appointments.text}</p>
          </div>
        )}
        {activeTab === "aufgabendetails" && (
          <div className="contact-details-tab">
            <h2>{mockData.tabContent.taskDetailsTitle}</h2>
            {mockData.taskDetailsInfo && (
              <>
                <WaitingListLeadBasicInformationTableV1
                  firstName={mockData.taskDetailsInfo.clientName.split(" ")[0]}
                  lastName={mockData.taskDetailsInfo.clientName.split(" ")[1]}
                  productName={mockData.taskDetailsInfo.relatedProduct}
                  clientAvailability={mockData.taskDetailsInfo.clientAvailability}
                  waitingListLeadState={mockData.taskDetailsInfo.taskState}
                  requestRegion={mockData.taskDetailsInfo.requestRegion}
                  requestZipCode={mockData.taskDetailsInfo.requestZipCode}
                  phoneNumber={getCommaSeparatedPhoneNumberFromArray(
                    mockData.taskDetailsInfo.phoneNumbers
                  )}
                  isClosedByClient={mockData.taskDetailsInfo.isClosedByClient}
                  isPersonalProduct={mockData.taskDetailsInfo.isPersonalProduct}
                />
                <WaitingListLeadProductCriteriaInformationTableV1
                  waitingListLeadCriteriaInfo={{
                    waitingListLeadState: mockData.taskDetailsInfo.taskState,
                    isClosedByClient: mockData.taskDetailsInfo.isClosedByClient,
                    isPersonalProduct: mockData.taskDetailsInfo.isPersonalProduct,
                    productCriteriaList: mockData.taskDetailsInfo.taskCriteriaList
                  }}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function TaskDetailOverview() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSupplierInfo();
  const [isRegisterForAMSet, setIsRegisterForAMSet] = useState(false);
  const [promotionModalOpen, setPromotionModal] = useState(false);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);

  // Neuer State für Aktivitäten, initial aus den Mock-Daten
  const [activities, setActivities] = useState<Activity[]>(mockData.tabContent.activities);

  useEffect(() => {
    const isSet = localStorage.getItem(isRegistrationForAm) !== null;
    setIsRegisterForAMSet(isSet);
  }, [navigate]);

  function getWaitingListLeadStatusView(
    state: WaitingListLeadState,
    isClosedByClient: boolean,
    isPersonalProduct: boolean
  ) {
    if (isClosedByClient) {
      if (state === WaitingListLeadState.Acquired) {
        return <WaitingListLeadStateAcquiredWithClosedDateV1 isPersonalProduct={isPersonalProduct} />;
      }
      return <WaitingListLeadStateClosed isPersonalProduct={isPersonalProduct} />;
    }
    switch (state) {
      case WaitingListLeadState.Open:
        return (
          <WaitingListLeadStateOpenV1
            secureId={taskId || ""}
            isPersonalProduct={isPersonalProduct}
            onSuccessCallback={() => setTimeout(() => showPromotionModal(), 2000)}
          />
        );
      case WaitingListLeadState.Acquired:
        return <WaitingListLeadStateAcquiredV1 isPersonalProduct={isPersonalProduct} />;
      default:
        return <WaitingListLeadStateRejectedV1 />;
    }
  }

  function showPromotionModal() {
    if (!isRegisterForAMSet && !isAuthenticated) {
      setPromotionModal(true);
    }
  }

  // Modal-Reihenfolge und dynamische Berechnung der leftOffset-Werte
  const modalOrder = ["email", "note", "call", "task"];
  const gap = 20;
  const startLeft = 50;

  const calculateModalOffsets = () => {
    let currentLeft = startLeft;
    const offsets: { [key: string]: number } = {};

    modalOrder.forEach((modalKey) => {
      let isOpen = false;
      switch (modalKey) {
        case "email":
          isOpen = emailModalOpen;
          break;
        case "note":
          isOpen = noteModalOpen;
          break;
        case "call":
          isOpen = callModalOpen;
          break;
        case "task":
          isOpen = taskModalOpen;
          break;
        default:
          break;
      }
      // Im geöffneten Zustand 500px, sonst 250px
      const width = isOpen ? 500 : 250;
      offsets[modalKey] = currentLeft;
      currentLeft += width + gap;
    });
    return offsets;
  };

  const modalOffsets = calculateModalOffsets();

  return (
    <div className="lead-detail-container">
      <BackToTopButton />
      <div className="detail-layout">
        <div className="sidebar-left">
          {mockData.taskDetailsInfo && (
            <>
              <div className="contact-details">
                <div className="contact-info">
                  <h2>{mockData.taskDetailsInfo.title}</h2>
                  <p>Status: {mockData.taskDetailsInfo.status}</p>
                  <p>Priorität: {mockData.taskDetailsInfo.priority}</p>
                  <p>Fällig am: {mockData.taskDetailsInfo.dueDate}</p>
                </div>
                <div className="action-buttons">
                  <button onClick={() => setCallModalOpen(true)}>
                    <i className="fa-solid fa-phone" />
                  </button>
                  <button onClick={() => setEmailModalOpen(true)}>
                    <i className="fa-solid fa-envelope" />
                  </button>
                  <button onClick={() => setNoteModalOpen(true)}>
                    <i className="fa-solid fa-sticky-note" />
                  </button>
                  <button onClick={() => setTaskModalOpen(true)}>
                    <i className="fa-solid fa-check" />
                  </button>
                </div>
              </div>
              <CollapsibleGroup title="Über diese Aufgabe">
                <ProductDetailCard
                  type="editabletext"
                  title="Titel"
                  content={mockData.taskDetailsInfo.title}
                />
                <ProductDetailCard
                  type="dropdown"
                  options={["Niedrig", "Mittel", "Hoch"]}
                  title="Priorität"
                  content={mockData.taskDetailsInfo.priority}
                />
                <ProductDetailCard
                  type="text"
                  title="Zugewiesen an"
                  content={mockData.taskDetailsInfo.assignedTo}
                />
                <ProductDetailCard
                  type="editabletext"
                  title="Beschreibung"
                  content={mockData.taskDetailsInfo.description}
                />
              </CollapsibleGroup>
              <CollapsibleGroup title="Über den Klienten">
                <ProductDetailCard
                  type="text"
                  title="Klientenname"
                  content={mockData.taskDetailsInfo.clientName}
                />
                <ProductDetailCard
                  type="text"
                  title="Verfügbarkeit"
                  content={mockData.taskDetailsInfo.clientAvailability}
                />
                <ProductDetailCard
                  type="text"
                  title="Bedarfsort"
                  content={mockData.taskDetailsInfo.clientLocation}
                />
              </CollapsibleGroup>
              <CollapsibleGroup title="Produktkriterien">
                <ProductDetailCard
                  type="text"
                  title="Produktname"
                  content={mockData.taskDetailsInfo.relatedProduct}
                />
                <ProductDetailCard
                  type="text"
                  title="Region"
                  content={mockData.taskDetailsInfo.requestRegion}
                />
                <ProductDetailCard
                  type="text"
                  title="PLZ"
                  content={mockData.taskDetailsInfo.requestZipCode}
                />
              </CollapsibleGroup>
            </>
          )}
        </div>
        <div className="main-content">
          <div className="main-content-header">
            <div className="main-content-header">
              <i className="fa-solid fa-arrow-left" />
              Vorherige
            </div>
            <h1 className="detail-number">
              Aufgabe: {mockData.taskDetailsInfo.taskNumber}
            </h1>
            <div className="main-content-header">
              Nächste
              <i className="fa-solid fa-arrow-right" />
            </div>
          </div>

          <MiddleSection
            getWaitingListLeadStatusView={getWaitingListLeadStatusView}
            taskInfo={{
              title: mockData.taskDetailsInfo.title,
              status: mockData.taskDetailsInfo.status,
              priority: mockData.taskDetailsInfo.priority,
              dueDate: mockData.taskDetailsInfo.dueDate,
              assignedTo: mockData.taskDetailsInfo.assignedTo,
              clientName: mockData.taskDetailsInfo.clientName,
              clientAvailability: mockData.taskDetailsInfo.clientAvailability,
              phoneNumbers: mockData.taskDetailsInfo.phoneNumbers,
              email: "test@mail.de"
            }}
            activities={activities}
          />
        </div>
        <div className="sidebar-right">
          <GroupSection title="Zugehörige Aufgaben" items={mockData.relatedItems} />
          <GroupSection title="Gesendete E-Mails" items={mockData.emailItems} />
          <GroupSection title="Nächste Schritte" items={mockData.nextActionItems} />
        </div>
      </div>
      {/* Alle Modals werden als separate Komponenten angezeigt – dynamisch angeordnet */}
      <EmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        leftOffset={modalOffsets.email}
        onEmailSent={(activity) => setActivities((prev) => [...prev, activity])}
        onOpen={() => setEmailModalOpen(true)}
      />
      <NoteModal
        open={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        onOpen={() => setNoteModalOpen(true)}
        leftOffset={modalOffsets.note}
        onNoteAdded={(activity) => setActivities((prev) => [...prev, activity])}
      />
      <CallModal
        open={callModalOpen}
        onClose={() => setCallModalOpen(false)}
        onOpen={() => setCallModalOpen(true)}
        leftOffset={modalOffsets.call}
        onCallAdded={(activity) => setActivities((prev) => [...prev, activity])}
      />
      <TaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onOpen={() => setTaskModalOpen(true)}
        leftOffset={modalOffsets.task}
        onTaskAdded={(activity) => setActivities((prev) => [...prev, activity])}
      />
    </div>
  );
}

export default TaskDetailOverview;
